import React, { Fragment, useState } from 'react';
import { Button, Modal, ProgressBar } from 'react-bootstrap';
import { MdOutlineFileDownload } from 'react-icons/md';
import { css } from 'twin.macro';
import { fetchWrapper } from '@/_helpers';
import LoadingSpiner from '@/_elements/LoadingSpiner';
import { downloadBulkUserFormDataPdfs, generateSubmittedFormUrl } from './utils';
import { toast } from 'react-toastify';

const BulkFormsDownload = ({ disabled, filters = {}, pdfNamePrefix = '', singleFile = true }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [totalResults, setTotalResults] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);
    const [filesDownloaded, setFilesDownloaded] = useState(0);
    const [totalFilesToDownload, setTotalFilesToDownload] = useState(0);
    const [userFormDatas, setUserFormDatas] = useState([]);
    const downloadLimit = filters?.limit;

    const handleResetState = () => {
        setIsDownloading(false);
        setFilesDownloaded(0);
        setTotalFilesToDownload(0);
    };

    const handleShow = () => {
        handleResetState();
        setShow(true);
    };

    const handleClose = () => setShow(false);

    const fetchData = async () => {
        try {
            setLoading(true);
            const fetchUrl = generateSubmittedFormUrl(filters);
            const response = await fetchWrapper.get(fetchUrl);
            setUserFormDatas(response?.results || []);
            setTotalResults(response?.totalResults);
        } catch (error) {
            if (error?.name === 'AbortError') {
                return;
            }
            toast.error(error?.message);
            console.error('Error fetching total forms count:', error);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        if (show) {
            fetchData();
        } else {
            setUserFormDatas([]);
        }
    }, [show]);

    const handleStartDownload = async () => {
        handleResetState();
        setIsDownloading(true);
        try {
            await downloadBulkUserFormDataPdfs({
                userFormDatas,
                pdfNamePrefix,
                singleFile,
                onProgress: (count) => setFilesDownloaded(count),
                setTotalCount: (total) => setTotalFilesToDownload(total),
            });
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Fragment>
            <Button disabled={disabled} onClick={handleShow}>
                <MdOutlineFileDownload className="mr-1" />
                Bulk Download
            </Button>
            <Modal show={show} centered backdrop="static" size="xl">
                <Modal.Header>
                    <Modal.Title id="contained-modal-title-vcenter">Bulk Form Download</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div>
                        {loading ? (
                            <div className="d-flex justify-content-center">
                                <LoadingSpiner size={40} />
                            </div>
                        ) : (
                            <div>
                                <p>
                                    Total Results Found:{' '}
                                    {totalResults && totalResults > downloadLimit ? `>=${downloadLimit}` : totalResults}
                                </p>
                                <p>Download Limit: {downloadLimit}</p>
                                <div className="d-flex justify-content-center">
                                    {isDownloading && (
                                        <div style={{ marginTop: '50px', marginBottom: '50px', width: '80%' }}>
                                            <LoadingSpiner size={40} />
                                            <ProgressBar
                                                animated
                                                min={0}
                                                now={Number(
                                                    `${((filesDownloaded || 0) / (totalFilesToDownload || 1)) * 100}`
                                                )}
                                                label={`${Math.round(((filesDownloaded || 0) / (totalFilesToDownload || 1)) * 100)} %`}
                                                css={css`
                                                    height: 30px;
                                                    font-size: 24px;
                                                    width: 100%;
                                                    margin-top: 30px;
                                                `}
                                            />
                                            <div className="text-center mt-2">{`( ${filesDownloaded} / ${totalFilesToDownload} ) forms processed ...`}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="d-flex justify-content-center mt-4">
                        {!isDownloading && (
                            <Button disabled={isDownloading || loading} onClick={handleStartDownload}>
                                <span className="mr-2">Start Download</span>
                            </Button>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={isDownloading || loading}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

export default BulkFormsDownload;
